import { useFeatureAccess } from './useFeatureAccess';

/**
 * FeatureGate — declarative claim-feature gate (SA-1592 Phase A0).
 *
 * Renders `children` only when the given claim feature is active for the
 * current company; otherwise renders `fallback`. Mirrors `Can`'s guard-clause
 * shape, supports `children` as a render function `(isActive) => ReactNode`,
 * and renders no wrapper element (DOM parity). Fail-closed on an unknown
 * feature.
 */

// Public feature vocabulary consumed by call sites.
export const FEATURE = {
  CASHLESS: 'CASHLESS',
  REIMBURSEMENT: 'REIMBURSEMENT',
  ANY: 'ANY',
};

// Feature -> useFeatureAccess flat-return key.
const FEATURE_FLAG = {
  [FEATURE.CASHLESS]: 'isCashlessActive',
  [FEATURE.REIMBURSEMENT]: 'isReimbursementActive',
  [FEATURE.ANY]: 'isAnyClaimFeatureActive',
};

/**
 * @param {object} props
 * @param {string} props.feature - a FEATURE enum value.
 * @param {React.ReactNode} [props.fallback=null] - rendered when feature inactive.
 * @param {React.ReactNode | ((isActive: boolean) => React.ReactNode)} props.children
 */
const FeatureGate = ({ feature, fallback = null, children }) => {
  const access = useFeatureAccess();
  const flag = FEATURE_FLAG[feature];
  const isActive = Boolean(flag) && access[flag] === true;

  if (typeof children === 'function') return children(isActive);

  if (!isActive) return fallback;
  return children;
};

export default FeatureGate;
